import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TemperatureEquipment } from '../../types/temperatureEquipment';
import temperatureEquipmentApiService from '../../services/temperatureEquipmentApiService';

interface TemperatureEquipmentState {
  equipment: TemperatureEquipment[];
  loading: boolean;
  error: string | null;
}

const initialState: TemperatureEquipmentState = {
  equipment: [],
  loading: false,
  error: null,
};

export const fetchTemperatureEquipment = createAsyncThunk(
  'temperatureEquipment/fetch',
  async (type: string) => {
    return await temperatureEquipmentApiService.fetchByType(type);
  }
);

export const createTemperatureEquipment = createAsyncThunk(
  'temperatureEquipment/create',
  async (equipment: Partial<TemperatureEquipment>) => {
    return await temperatureEquipmentApiService.createEquipment(equipment);
  }
);

export const updateTemperatureEquipment = createAsyncThunk(
  'temperatureEquipment/update',
  async ({ id, equipment }: { id: string; equipment: Partial<TemperatureEquipment> }) => {
    return await temperatureEquipmentApiService.updateEquipment(id, equipment);
  }
);

export const deactivateTemperatureEquipment = createAsyncThunk(
  'temperatureEquipment/deactivate',
  async (id: string) => {
    await temperatureEquipmentApiService.deactivateEquipment(id);
    return id;
  }
);

const temperatureEquipmentSlice = createSlice({
  name: 'temperatureEquipment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemperatureEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemperatureEquipment.fulfilled, (state, action: PayloadAction<TemperatureEquipment[]>) => {
        state.loading = false;
        state.equipment = action.payload;
      })
      .addCase(fetchTemperatureEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch temperature equipment';
      });

    builder
      .addCase(createTemperatureEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemperatureEquipment.fulfilled, (state, action: PayloadAction<TemperatureEquipment>) => {
        state.loading = false;
        state.equipment.push(action.payload);
      })
      .addCase(createTemperatureEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create temperature equipment';
      });

    builder
      .addCase(updateTemperatureEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemperatureEquipment.fulfilled, (state, action: PayloadAction<TemperatureEquipment>) => {
        state.loading = false;
        const index = state.equipment.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.equipment[index] = action.payload;
        }
      })
      .addCase(updateTemperatureEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update temperature equipment';
      });

    builder
      .addCase(deactivateTemperatureEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateTemperatureEquipment.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.equipment = state.equipment.filter((e) => e.id !== action.payload);
      })
      .addCase(deactivateTemperatureEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to deactivate temperature equipment';
      });
  },
});

export const { clearError } = temperatureEquipmentSlice.actions;
export default temperatureEquipmentSlice.reducer;
